// src/pages/TutorialPage.js
import React from 'react';
import { Box, Typography, Link as MuiLink, CardMedia } from '@mui/material';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import ReactMarkdown from 'react-markdown';
import { tutItems } from '../data/SiteData';

const TutorialPage = () => {
  const { tutId } = useParams();

  const tutorial = tutItems.find((item) => item.tutId === tutId);
  if (!tutorial) return <Typography>Tutorial not found.</Typography>;

  const isVideo = tutorial.tutType === 'video';
  const formattedDate = tutorial.date || '';

  const breadcrumbItems = [
    { title: 'Home', path: '/' },
    { title: 'Resources', path: '/resources' },
    { title: 'Videos & Tutorials', path: '/resources/videos-tutorials' },
    {
      title:
        tutorial.title.length > 30
          ? tutorial.title.substring(0, 30) + '...'
          : tutorial.title,
      path: tutorial.link,
    },
  ];

  return (
    <>
      <Navbar />
      <HeroBanner
        heroTitle="Tutorials & Videos"
        heroDescription="Learn more about Digital Twins tools and workflows."
        imageUrl="/images/digiTwins.png"
        height="165px"
      />
      <Breadcrumb items={breadcrumbItems} />

      <Box sx={{ padding: '32px', maxWidth: '1300px', margin: '0 auto' }}>

        {/* ============================ */}
        {/* VIDEO TYPE LAYOUT */}
        {/* ============================ */}
        {isVideo ? (
          <>

            {/* Title */}
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: '24px',
                color: '#142434',
                mb: 2,
              }}
            >
              {tutorial.title}
            </Typography>

            {/* Embedded Video */}
            {tutorial.extLink && (
              <Box sx={{ mb: 3 }}>
                <iframe
                  src={tutorial.extLink}
                  title={tutorial.title}
                  width="100%"
                  height="500"
                  frameBorder="0"
                  allowFullScreen
                />
              </Box>
            )}

            {/* Date */}
            <Typography
              sx={{
                fontFamily: 'PT Sans, sans-serif',
                fontSize: '14px',
                color: '#142434',
                mb: 2,
              }}
            >
              {formattedDate}
            </Typography>

            {/* MainContent or Description */}
            <Box
  sx={{
    mt: 3,
    fontFamily: 'PT Sans, sans-serif',
    fontSize: '16px',
    lineHeight: 1.6,
    color: '#142434',
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontFamily: 'Poppins, sans-serif',
    },
    '& a': {
      color: '#489596',
      textDecoration: 'underline',
    },
  }}
>
  {tutorial.mainContent ? (
    <ReactMarkdown>{tutorial.mainContent}</ReactMarkdown>
  ) : (
    <Typography sx={{ fontFamily: 'PT Sans, sans-serif' }}>
      {tutorial.description}
    </Typography>
  )}
</Box>
          </>
        ) : (
          <>
            {/* ============================ */}
            {/* TUTORIAL (non-video) MATCHES NEWSPAGE LAYOUT */}
            {/* ============================ */}

            <Box sx={{ display: 'flex', gap: 4, mb: 4, flexWrap: 'wrap' }}>
              {tutorial.image && (
                <CardMedia
                  component="img"
                  src={tutorial.image}
                  alt={tutorial.title}
                  sx={{ width: 160, flexShrink: 0, borderRadius: '8px' }}
                />
              )}

              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                    fontSize: '24px',
                    color: '#142434',
                    mb: 1,
                  }}
                >
                  {tutorial.title}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: 'PT Sans, sans-serif',
                    fontSize: '14px',
                    color: '#142434',
                    mb: 1,
                  }}
                >
                  {formattedDate}
                </Typography>

                {tutorial.extLink && (
                  <MuiLink
                    href={tutorial.extLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: 14 }}
                  >
                    {tutorial.extLink}
                  </MuiLink>
                )}
              </Box>
            </Box>

            {tutorial.mainContent && (
              <Box
                sx={{
                  mt: 4,
                  fontFamily: 'PT Sans, sans-serif',
                  fontSize: '16px',
                  lineHeight: 1.6,
                  color: '#142434',
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    fontFamily: 'Poppins, sans-serif',
                  },
                  '& a': {
                    color: '#489596',
                    textDecoration: 'underline',
                  },
                }}
              >
                <ReactMarkdown>{tutorial.mainContent}</ReactMarkdown>
              </Box>
            )}
          </>
        )}
      </Box>

      <Footer />
    </>
  );
};

export default TutorialPage;
